// src/components/monthly/ReservationTableBody.tsx
import React from 'react';
import {
  startOfMonth,
  endOfMonth,
  max,
  min,
  isBefore,
  isAfter,
  endOfDay,
} from 'date-fns';
import { useLocation } from 'react-router-dom';

/**
 * ISO文字列 (例: "2025-01-31T00:00:00Z") から
 * 年月日だけを取り出し、開始日の場合は 0:00、終了日の場合は 23:59:59.999 として返す
 */
function parseDateOnly(isoString: string, isEnd = false): Date | null {
  if (!isoString) return null;
  const datePart = isoString.split('T')[0]; // "2025-01-31"
  const [y, m, d] = datePart.split('-').map(Number);
  if (!y || !m || !d) return null;

  let date = new Date(y, m - 1, d, 0, 0, 0);
  if (isNaN(date.getTime())) return null;

  if (isEnd) {
    date = endOfDay(date);
  }
  return date;
}

interface RentalItem {
  id: number;
  room_type_id: number;
  start_date: string;
  end_date: string;
  created_at: string;
  updated_at: string | null;
}

interface RangeData {
  month: string; // "YYYY.MM"
  range1: string; // "○" "△" "×"
  range2: string; // "○" "△" "×"
  range3: string; // "○" "△" "×"
}

interface MonthlyReservationTableBodyProps {
  monthlyRentals: RentalItem[];
}

export const MonthlyReservationTableBody: React.FC<
  MonthlyReservationTableBodyProps
> = ({ monthlyRentals }) => {
  // URL から言語判定
  const location = useLocation();
  const langSegment = location.pathname.split('/')[1];
  const useEnglish = langSegment === 'en';

  // 表示テキスト
  const texts = {
    noData: useEnglish ? 'No availability data' : '空き状況データがありません',
    early: useEnglish ? 'Early' : '初旬',
    mid: useEnglish ? 'Mid' : '中旬',
    late: useEnglish ? 'Late' : '下旬',
  };

  // 在庫がどの程度覆われているかを判定する関数
  const evaluateSegment = (
    segmentStart: Date,
    segmentEnd: Date,
    rentals: RentalItem[]
  ): string => {
    let fullyCovered = false;
    let partiallyCovered = false;

    rentals.forEach((r) => {
      if (!r.start_date || !r.end_date) return;

      const rentalStart = parseDateOnly(r.start_date, false);
      const rentalEnd = parseDateOnly(r.end_date, true);
      if (!rentalStart || !rentalEnd) return;

      const overlapStart = max([segmentStart, rentalStart]);
      const overlapEnd = min([segmentEnd, rentalEnd]);

      if (
        isBefore(overlapStart, overlapEnd) ||
        overlapStart.getTime() === overlapEnd.getTime()
      ) {
        // segment 全体が rental に覆われているか
        if (
          (isBefore(rentalStart, segmentStart) ||
            rentalStart.getTime() === segmentStart.getTime()) &&
          (isAfter(rentalEnd, segmentEnd) ||
            rentalEnd.getTime() === segmentEnd.getTime())
        ) {
          fullyCovered = true;
        } else {
          partiallyCovered = true;
        }
      }
    });

    if (fullyCovered) return '○';
    if (partiallyCovered) return '△';
    return '×';
  };

  // (1) rental から月(YYYY-MM) を収集
  const monthsSet = new Set<string>();
  monthlyRentals.forEach((r) => {
    if (!r.start_date || !r.end_date) return;
    const start = parseDateOnly(r.start_date, false);
    const end = parseDateOnly(r.end_date, true);
    if (!start || !end) return;

    let current = startOfMonth(start);
    while (!isAfter(current, end)) {
      // ローカルの日付から月キーを生成（タイムゾーンの影響を受けない）
      monthsSet.add(
        `${current.getFullYear()}-${String(current.getMonth() + 1).padStart(2, '0')}`
      );
      current = startOfMonth(
        new Date(current.getFullYear(), current.getMonth() + 1, 1)
      );
    }
  });

  // (2) 今月より前を除外し、今月から6か月分だけ抽出
  const now = new Date();
  const currentKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const monthsArray = Array.from(monthsSet).sort();
  const futureMonths = monthsArray.filter((m) => m >= currentKey);
  const monthsLimited = futureMonths.slice(0, 6);

  // (3) 初旬 / 中旬 / 下旬 の判定
  const data: RangeData[] = monthsLimited.map((yearMonth) => {
    const [year, mo] = yearMonth.split('-').map(Number);
    const monthStart = new Date(year, mo - 1, 1, 0, 0, 0);
    const monthEnd = endOfMonth(monthStart);

    const earlyStart = new Date(year, mo - 1, 1);
    const earlyEnd = new Date(year, mo - 1, 10, 23, 59, 59, 999);
    const midStart = new Date(year, mo - 1, 11);
    const midEnd = new Date(year, mo - 1, 20, 23, 59, 59, 999);
    const lateStart = new Date(year, mo - 1, 21);
    const lateEnd = monthEnd;

    const rentalsForMonth = monthlyRentals.filter((r) => {
      const rs = parseDateOnly(r.start_date, false);
      const re = parseDateOnly(r.end_date, true);
      if (!rs || !re) return false;
      // 月の範囲と全く重ならなければ除外
      return !(isAfter(rs, monthEnd) || isBefore(re, monthStart));
    });

    return {
      month: `${year}.${String(mo).padStart(2, '0')}`,
      range1: evaluateSegment(earlyStart, earlyEnd, rentalsForMonth),
      range2: evaluateSegment(midStart, midEnd, rentalsForMonth),
      range3: evaluateSegment(lateStart, lateEnd, rentalsForMonth),
    };
  });

  // (4) テーブル表示
  return (
    <div className="overflow-x-auto my-8 text-[10px]">
      {data.length === 0 ? (
        <p>{texts.noData}</p>
      ) : (
        <table className="min-w-full table-fixed border-collapse border border-black">
          <colgroup>
            {data.map((_, i) => (
              <col key={i} />
            ))}
          </colgroup>
          <thead>
            <tr>
              {data.map((item, i) => (
                <th
                  key={i}
                  className="pl-2 py-2 border border-black text-start text-[10px] font-normal"
                  style={{ paddingRight: '4.375rem' }}
                >
                  {item.month}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              {data.map((item, i) => (
                <td key={i} className="border border-black text-center">
                  <div className="flex">
                    {/* 初旬 or Early */}
                    <div
                      className={`flex-1 flex flex-col border-black py-1 ${
                        item.range1 === '×'
                          ? 'bg-[#F4F3E6]'
                          : item.range1 === '○'
                            ? 'bg-white'
                            : ''
                      }`}
                    >
                      <div className="w-full">{texts.early}</div>
                      <div className="w-full py-1 text-base font-bold">
                        {item.range1}
                      </div>
                    </div>
                    {/* 中旬 or Mid */}
                    <div
                      className={`flex-1 flex flex-col border-l border-r border-black py-1 ${
                        item.range2 === '×'
                          ? 'bg-[#F4F3E6]'
                          : item.range2 === '○'
                            ? 'bg-white'
                            : ''
                      }`}
                    >
                      <div className="w-full">{texts.mid}</div>
                      <div className="w-full py-1 text-base font-bold">
                        {item.range2}
                      </div>
                    </div>
                    {/* 下旬 or Late */}
                    <div
                      className={`flex-1 flex flex-col border-black py-1 ${
                        item.range3 === '×'
                          ? 'bg-[#F4F3E6]'
                          : item.range3 === '○'
                            ? 'bg-white'
                            : ''
                      }`}
                    >
                      <div className="w-full">{texts.late}</div>
                      <div className="w-full py-1 text-base font-bold">
                        {item.range3}
                      </div>
                    </div>
                  </div>
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      )}
    </div>
  );
};
